import { newAmount } from '@orus.eu/amount'

import { createPolicyItemConfigurationResolver } from '../../../model/policy-item-configuration.ts'
import type { PolicyItems, PossibleInsuranceLimitOfIndemnity } from '../../../model/policy-item.ts'
import type { MrpaPolicyItems, MrpaPolicyItemsState } from '../mrpa-insurance.ts'

const oneHundred = newAmount(100)
const twoHundredFifty = newAmount(250)
const fiveHundred = newAmount(500)
const sevenHundredFifty = newAmount(750)
const oneThousand = newAmount(1_000)
const twoThousand = newAmount(2_000)
const oneThousandFiveHundred = newAmount(1_500)
const twoThousandFiveHundred = newAmount(2_500)
const threeThousand = newAmount(3_000)
const fiveThousand = newAmount(5_000)
const tenThousand = newAmount(10_000)
const twentyThousand = newAmount(20_000)
const thirtyThousand = newAmount(30_000)
const twentyFiveThousand = newAmount(25_000)
const fiftyThousand = newAmount(50_000)
const oneHundredThousand = newAmount(100_000)
const twoHundredFiftyThousand = newAmount(250_000)
const threeHundredThousand = newAmount(300_000)
const fiveHundredThousand = newAmount(500_000)
const oneMillion = newAmount(1_000_000)
const twoMillion = newAmount(2_000_000)
const threeMillion = newAmount(3_000_000)
const fiveMillion = newAmount(5_000_000)
const sixMillion = newAmount(6_000_000)
const eightMillion = newAmount(8_000_000)
const tenMillion = newAmount(10_000_000)

const defaultPossibleLimitOfIndemnities = {
  type: 'amounts',
  amounts: [twoThousandFiveHundred, fiveThousand, tenThousand, twentyFiveThousand],
} as const satisfies PossibleInsuranceLimitOfIndemnity

export const mrpaPolicyItemsV1_0_0 = {
  batiment: {
    name: 'batiment',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'text', value: 'Valeur de reconstruction à neuf' },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [fiveHundred, sevenHundredFifty, oneThousand, twoThousandFiveHundred, fiveThousand],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    additionalInfo: {
      limitOfIndemnity: 'Valeur de reconstruction à neuf',
      deductible: null,
    },
    dependsOn: [],
  },
  contenu: {
    name: 'contenu',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'calculatedAmount' },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [fiveHundred, sevenHundredFifty, oneThousand, twoThousandFiveHundred, fiveThousand],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  rce: {
    name: 'rce',
    type: 'guarantee',
    optionality: ['included', 'optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [sixMillion, eightMillion, tenMillion],
    },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [
        fiveHundred,
        sevenHundredFifty,
        oneThousand,
        oneThousandFiveHundred,
        twoThousandFiveHundred,
        fiveThousand,
      ],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  assistance: {
    name: 'assistance',
    type: 'service',
    optionality: ['included'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'none' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: true,
        canBeSelected: false,
        canBeUpdated: false,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  materielInfoNonPortable: {
    name: 'materielInfoNonPortable',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: defaultPossibleLimitOfIndemnities,
    possibleDeductibles: {
      type: 'amounts',
      amounts: [twoHundredFifty, fiveHundred, oneThousand],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  brisMaterielInfo: {
    name: 'brisMaterielInfo',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [tenThousand, twentyFiveThousand, fiftyThousand],
    },
    possibleDeductibles: { type: 'calculatedAmount' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  marchandisesSousTemperature: {
    name: 'marchandisesSousTemperature',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [twoThousandFiveHundred, fiveThousand, tenThousand],
    },
    possibleDeductibles: { type: 'fixedAmount', amount: oneHundred },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  pe: {
    name: 'pe',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'timesInMonths', values: [12, 18, 24] },
    possibleDeductibles: { type: 'fixedTimeInDays', value: 3 },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  peLoyer: {
    name: 'peLoyer',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'timesInMonths', values: [12, 18, 24] },
    possibleDeductibles: { type: 'fixedTimeInDays', value: 3 },
    dependsOn: ['pe'],
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    additionalInfo: null,
  },
  peCarence: {
    name: 'peCarence',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [fiftyThousand, oneHundredThousand, twoHundredFiftyThousand],
    },
    possibleDeductibles: { type: 'fixedTimeInDays', value: 1 },
    dependsOn: ['pe'],
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    additionalInfo: null,
  },
  peAccess: {
    name: 'peAccess',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'fixedAmount', amount: fiftyThousand },
    possibleDeductibles: { type: 'fixedTimeInDays', value: 2 },
    dependsOn: ['pe'],
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    additionalInfo: null,
  },
  rcal: {
    name: 'rcal',
    type: 'guarantee',
    optionality: ['included', 'optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [twoMillion, threeMillion, fiveMillion],
    },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [oneThousandFiveHundred, twoThousand, twoThousandFiveHundred, threeThousand, fiveThousand, tenThousand],
    },
    editConfiguration: ({ state, context }) => {
      switch (context) {
        case 'selfonboarding': {
          return {
            isHidden: false,
            canBeSelected: false,
            canBeUpdated: false,
          }
        }
        case 'backoffice': {
          const isAlwaysIncluded = state.mrpaIsManufacturingSegment
          return {
            isHidden: false,
            canBeSelected: !isAlwaysIncluded, // Not "selectable" when always "true"
            canBeUpdated: true,
          }
        }
      }
    },
    dependsOn: [],
    additionalInfo: null,
  },
  rcalDinc: {
    name: 'rcalDinc',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [oneHundredThousand, twoHundredFiftyThousand, threeHundredThousand, fiveHundredThousand],
    },
    possibleDeductibles: { type: 'amounts', amounts: [twoThousandFiveHundred, fiveThousand] },
    dependsOn: ['rcal'],
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    additionalInfo: null,
  },
  rcalUs: {
    name: 'rcalUs',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [twoHundredFiftyThousand, fiveHundredThousand, oneMillion],
    },
    possibleDeductibles: { type: 'fixedAmount', amount: tenThousand },
    dependsOn: ['rcal'],
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    additionalInfo: null,
  },
  materielInfoPortable: {
    name: 'materielInfoPortable',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: defaultPossibleLimitOfIndemnities,
    possibleDeductibles: { type: 'amounts', amounts: [twoHundredFifty, fiveHundred] },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },

  marchandisesTransport: {
    name: 'marchandisesTransport',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [tenThousand, twentyThousand, thirtyThousand],
    },
    possibleDeductibles: { type: 'amounts', amounts: [twoHundredFifty, fiveHundred, oneThousand] },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: true,
        canBeSelected: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
  brisDeMachine: {
    name: 'brisDeMachine',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: [tenThousand, twentyFiveThousand, fiftyThousand],
    },
    possibleDeductibles: { type: 'calculatedAmount' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeSelected: true,
        canBeUpdated: true,
      }),
    }),
    dependsOn: [],
    additionalInfo: null,
  },
} as const satisfies PolicyItems<MrpaPolicyItems, MrpaPolicyItemsState>
